/**
 * Credit Note document — branded printable template shared by BOTH
 * directions (Client Refund / Vendor Credit). Mirrors InvoicePreview.js's
 * visual language (teal accent #34D0CB, Cormorant Garamond headings,
 * Outfit body) so it reads as the SAME document family, not a bolted-on
 * template. Party block strictly branches on `direction` — a client
 * document never sees vendor fields and vice versa.
 */
import React from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABEL = {
  cancellation: 'Cancellation', overpayment_correction: 'Overpayment Correction',
  service_issue: 'Service Issue', vendor_cancellation: 'Vendor Cancellation',
  service_not_delivered: 'Service Not Delivered', overbilling_correction: 'Overbilling Correction',
  other: 'Other',
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CreditNoteDocument = ({ data }) => {
  if (!data) return null;
  const { company = {}, party = {}, reference = {}, taxBreakdown = {} } = data;
  const isClient = data.direction === 'client_refund';

  return (
    <div className="max-w-4xl mx-auto bg-white border border-[#E5E7EB] shadow-sm" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="credit-note-document">
      <div style={{ height: '4px', background: '#34D0CB' }} />

      {/* Header */}
      <div className="px-10 py-6 bg-white border-b border-[#E5E7EB]">
        <div className="flex justify-between items-start gap-6">
          <div className="flex items-start gap-4">
            <img
              src={company.logoUrl ? (company.logoUrl.startsWith('http') ? company.logoUrl : `${BACKEND_URL}${company.logoUrl}`) : `${BACKEND_URL}/api/uploads/travcations-logo.png`}
              alt={company.name || 'Travcations'} className="h-8 w-auto object-contain" crossOrigin="anonymous"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div>
              {company.name && <p className="text-base font-semibold text-[#111827] leading-tight">{company.name}</p>}
              {company.address && <p className="text-[11px] text-[#6B7280] max-w-xs mt-1" style={{ lineHeight: 1.3 }}>{company.address}</p>}
              <div className="flex gap-3 mt-1 text-[11px] text-[#6B7280] flex-wrap">
                {company.phone && <span>Tel: {company.phone}</span>}
                {company.email && <span>{company.email}</span>}
              </div>
              {company.gstNumber && <p className="text-[10px] text-[#6B7280] mt-1 font-mono">GSTIN: {company.gstNumber}</p>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="uppercase text-[10px] tracking-[0.2em] font-semibold" style={{ color: '#34D0CB' }} data-testid="credit-note-direction-label">
              {isClient ? 'Client Credit Note' : 'Vendor Credit Note'}
            </p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-2">Credit Note No.</p>
            <p className="text-lg font-mono font-semibold leading-tight" style={{ color: '#111827' }} data-testid="credit-note-number">{data.creditNoteNumber || '—'}</p>
            <p className="text-xs text-[#6B7280] mt-1">Date: {formatDate(data.date)}</p>
            <p className="text-[10px] uppercase tracking-wide mt-2 font-semibold text-emerald-600">{(data.status || '').toUpperCase()}</p>
          </div>
        </div>
      </div>

      {/* Party */}
      <div className="px-10 py-4 border-b border-[#E5E7EB]" data-testid="credit-note-party">
        <p className="uppercase text-[9px] tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1.5">
          {isClient ? 'Issued To (Client)' : 'Issued To (Vendor)'}
        </p>
        <div className="text-xs text-[#6B7280]" style={{ lineHeight: 1.3 }}>
          <p className="text-sm font-medium text-[#1A1A1A]" data-testid="credit-note-party-name">
            {party.name || '—'}
            {isClient && party.ucc && <span className="font-normal text-[#6B7280]"> | UCC: <span className="font-mono">{party.ucc}</span></span>}
          </p>
          {party.address && <p>{party.address}</p>}
          {!isClient && party.gstNumber && <p className="font-mono">GSTIN: {party.gstNumber}</p>}
          {(party.email || party.phone) && <p>{[party.email, party.phone].filter(Boolean).join(' | ')}</p>}
        </div>
        <p className="text-xs text-[#6B7280] mt-2">
          {isClient
            ? <>Ref: Invoice {reference.invoiceNumber || '—'} · Trip {reference.tripRef || '—'}</>
            : <>Booking Ref: {reference.bookingRef || '—'}</>}
        </p>
      </div>

      {/* Amount + reason */}
      <div className="px-10 py-5 border-b border-[#E5E7EB]">
        <table className="w-full text-sm" data-testid="credit-note-amount-table">
          <tbody>
            <tr className="border-b border-[#F3F4F6]">
              <td className="py-2 text-[#6B7280]">Reason</td>
              <td className="py-2 text-right text-[#111827]">{REASON_LABEL[data.reason] || data.reason}</td>
            </tr>
            {data.reasonDetail && (
              <tr className="border-b border-[#F3F4F6]">
                <td className="py-2 text-[#6B7280]">Detail</td>
                <td className="py-2 text-right text-[#111827]">{data.reasonDetail}</td>
              </tr>
            )}
            {taxBreakdown.applicable && (
              <>
                <tr className="border-b border-[#F3F4F6]">
                  <td className="py-2 text-[#6B7280]">Base Amount Reversed</td>
                  <td className="py-2 text-right text-[#111827]" data-testid="credit-note-base-reversal">{inr(taxBreakdown.baseAmount)}</td>
                </tr>
                <tr className="border-b border-[#F3F4F6]">
                  <td className="py-2 text-[#6B7280]">GST Reversed</td>
                  <td className="py-2 text-right text-[#111827]" data-testid="credit-note-gst-reversal">{inr(taxBreakdown.gstAmount)}</td>
                </tr>
                <tr className="border-b border-[#F3F4F6]">
                  <td className="py-2 text-[#6B7280]">TCS Reversed</td>
                  <td className="py-2 text-right text-[#111827]" data-testid="credit-note-tcs-reversal">{inr(taxBreakdown.tcsAmount)}</td>
                </tr>
              </>
            )}
            <tr>
              <td className="py-3 font-semibold text-[#111827]">Total Credit Amount</td>
              <td className="py-3 text-right font-semibold text-lg" style={{ color: '#34D0CB' }} data-testid="credit-note-total-amount">{inr(data.amount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-10 py-4 text-[10px] text-[#9CA3AF]">
        <p>Issued by: {data.issuedBy || '—'}</p>
        <p className="mt-1">This Credit Note is generated for accounting and audit purposes.</p>
      </div>
    </div>
  );
};

export default CreditNoteDocument;
